<template lang="html">
  <v-row no-gutters>
    <FlvSplitPercent
      v-model="splitPercent"
      :total-value="Number(data.flv)"
    />

    <v-col
      cols="12"
      class="text-h6 pb-0"
      mt-4
    >
      Member spouse
    </v-col>

    <flv-values :values="memberValuesOne" />

    <v-col
      cols="12"
      class="py-0 pb-0"
    >
      <apexcharts
        ref="graph_member_one"
        class="no-print"
        width="100%"
        :height="250"
        :options="memberChartOneOptions"
        :series="memberSeriesOne"
      />
    </v-col>

    <flv-values :values="memberValuesTwo" />

    <v-col
      v-if="memberSeriesTwo.length > 0"
      cols="12"
      class="py-0 pb-0"
    >
      <apexcharts
        v-if="memberSeriesTwo.length > 0"
        ref="graph_member_two"
        class="no-print"
        width="100%"
        :height="250"
        :options="memberChartTwoOptions"
        :series="memberSeriesTwo"
      />
    </v-col>
      
    <v-col
      class="text-h6 pb-0"
      mt-4
    >
      Non-member spouse
    </v-col>

    <flv-values :values="nonMemberValues" />
        
    <v-col
      cols="12"
      class="py-0 pb-0"
    >
      <apexcharts
        ref="graph_nonmember"
        class="no-print"
        width="100%"
        :height="250"
        :options="nonMemberChartOptions"
        :series="nonMemberSeries"
      />
    </v-col>
  </v-row>
</template>

<script>
import helpers from '@/mixins/helpers'
import VueApexCharts from 'vue-apexcharts'
import chartOptions from './chart-options'
import growthShared from './growth-shared'


export default {
  components: {
    apexcharts: VueApexCharts
  },
  mixins: [ helpers, chartOptions, growthShared ],
  props: {
    data: Object,
    tables: Object,
  },
  data () {
    return {
      splitPercent: 0,
      nonMemberSeries: [],
      memberSeriesOne: [],
      memberSeriesTwo: [],
    }
  },
  computed: {
    labels () {
      return {
        nonMember: {
          separationAmount: 'Separation amount',
          fundedComponent: 'Funded component of separation amount',
          unfundedComponent: 'Unfunded component of separation amount',
        },
        member: {
          benefitReductionRate: 'Benefit rate of reduction',
          totalBenefitPost: 'Total benefit - post reduction',
          abm: 'Accrued Benefit Multiple (ABM) - post reduction',
          exessContributionsMultiple: 'Excess contributions multiple - post reduction',
          amc: 'Member component - post reduction',
          apc: 'Productivity component - post reduction',
          employerComponent: 'Employer component',
          ata: 'Accumulated Transfer Amounts (ATA) - post reduction',
          bam: 'Benefit accrual multiple - reduction amount',
          udb: 'Unfunded preserved benefit (UDB) - post reduction',
          fdb: 'Funded preserved benefit (FDB) - post reduction',
          fls_ass: 'Funded component of non-member spouse interest (FLS) - post reduction',
          ufls_ass: 'Unfunded component of non-member spouse interest (UFLS) - post reduction',
          bamPost: 'Benefit accrual multiple - post reduction',
        }
      }
    },
    reportType () {
      let { data: { valuation_type } } = this
      if (['pss_contributing'].includes(valuation_type)) return 'contributor'
      if (['pss_preserved_pension', 'pss_preserved_x_pension'].includes(valuation_type)) return 'preserved'
      if (['pss_associate'].includes(valuation_type)) return 'associate_preserved'
       return null
    },
    claimTypes () {
      return [
        { text: 'Age Retirement (incl. redundancy) ', value: 'age' },
        { text: 'Invalidity', value: 'invalid' },
      ]
    },
    memberAge () {
      let { data: { relevant_date, member_birth_date } } = this
      return this._calculateAge(member_birth_date, relevant_date)
    },
    ageFactor () {
      let pensionType = this.claimType.value
      let table = this.tables.age_factors || {}
      return (table[this.claimAge] || {})[pensionType]
    },
    fundedComponent () {
      let { funded_component, fls_ass, valuation_type } = this.data
      return valuation_type === 'pss_associate' ? fls_ass : funded_component
    },
    memberValuesOne () {
      let { _$, labels, colors, reportType } = this 
      let sfGross = this.calcSeparationFactorGross()
      let primary = []
      let secondary = []

      switch (reportType) {
        case 'contributor':
          primary = [
            { label: labels.member.totalBenefitPost, value: _$(this.calcTotalBenefitPost(sfGross), 0), color: colors[4]},
          ]
          secondary = [
            { label: labels.member.amc, value: _$(this.calcComponent('amc', sfGross), 0), color: colors[0]},
            { label: labels.member.apc, value: _$(this.calcComponent('apc', sfGross), 0), color: colors[1]},
            { label: labels.member.employerComponent, value: _$(this.calcComponent('employerComponent', sfGross), 0), color: colors[2]},
            { label: labels.member.ata, value: _$(this.calcComponent('ata', sfGross), 0), color: colors[3]},
          ]
          break;
        case 'preserved':
          primary = [
            { label: labels.member.totalBenefitPost, value: _$(this.calcTotalBenefitPost(sfGross), 0), color: colors[3]},
          ]
          secondary = [
            { label: labels.member.udb, value: _$(this.calcComponent('udb_pss', sfGross), 0), color: colors[0]},
            { label: labels.member.fdb, value: _$(this.calcComponent('fdb_pss', sfGross), 0), color: colors[1]},
            { label: labels.member.ata, value: _$(this.calcComponent('ata', sfGross), 0), color: colors[2]},
          ]
          break;
        case 'associate_preserved':
          primary = [
            { label: labels.member.totalBenefitPost, value: _$(this.calcTotalBenefitPost(sfGross), 0), color: colors[2]},
          ]
          secondary = [
            { label: labels.member.fls_ass, value: _$(this.calcComponent('fls_ass', sfGross), 0), color: colors[0]},
            { label: labels.member.ufls_ass, value: _$(this.calcComponent('ufls_ass', sfGross), 0), color: colors[1]},
          ]
          break;
        default:
          primary = [
            { label: labels.member.totalBenefitPost, value: `${this.calcBenefitReductionRate(sfGross).toFixed(2)}%`, color: colors[0]},
          ]
      }
      return { primary, secondary }
    },
    memberValuesTwo () {
      let { labels, colors, reportType } = this 
      let sfGross = this.calcSeparationFactorGross()
      let primary = []
      let secondary = []

      switch (reportType) {
        case 'contributor':
          primary = [
            { label: labels.member.bamPost, value: (this.calcBamReductionPost().toFixed(8)), color: colors[0] },
          ]
          secondary = [
            { label: labels.member.bam, value: (this.calcBamReduction(sfGross).toFixed(8)), },
          ]
        break;
      }
      return { primary, secondary }
    },
    memberChartOneOptions () {
      let yFormatter = v => this._$(v, 0)
      let yMax = this.calcTotalBenefitPost(0)

      return this._chartOptions({
        ...this.sharedChartOptions,
        id: 'graph_member_one',
        stacked: true,
        yMax,
        yFormatter
      })
    },
    memberChartTwoOptions () {
      let yFormatter = v => this._$(v, 0)
      let yMax 
      if (this.reportType === 'contributor') yFormatter = v => v.toFixed(5)

      return this._chartOptions({
        ...this.sharedChartOptions,
        id: 'graph_member_two',
        yFormatter,
        yMax
      })
    },
    bam () {
      let { data: { abm, excess_contributions_multiple } } = this
      return Number(abm) + Number(excess_contributions_multiple)
    }
  },
  beforeMount () {
    this.graphNonMember()
    this.graphMemberOne()
  },
  mounted () {
    this.graphMemberTwo()
  },
  methods: {
    graphMemberOne () {
      let { labels, reportType } = this

      // let benefitReductionRateSeries = { name: labels.member.benefitReductionRate, type: 'line', data: [] }

      let amcSeries = { name: labels.member.amc, type: 'area', data: [] }
      let apcSeries = { name: labels.member.apc, type: 'area', data: [] }
      let ataSeries = { name: labels.member.ata, type: 'area', data: [] }
      let udbSeries = { name: labels.member.udb, type: 'area', data: [] }
      let fdbSeries = { name: labels.member.fdb, type: 'area', data: [] }
      let flsSeries = { name: labels.member.fls_ass, type: 'area', data: [] }
      let uflsSeries = { name: labels.member.ufls_ass, type: 'area', data: [] }
      let employerComponentSeries = { name: labels.member.employerComponent, type: 'area', data: [] }
     
      let totalBenefitSeries = { name: labels.member.totalBenefitPost, type: 'line', data: [] }

      this.xValues.forEach(percent => {
        let separationAmount = this.calcSeparationAmount(percent)
        let sfGross = this.calcSeparationFactorGross(separationAmount)
  
        // benefitReductionRateSeries.data.push(this.calcBenefitReductionRate(sfGross))
        
        amcSeries.data.push(this.calcComponent('amc', sfGross))
        apcSeries.data.push(this.calcComponent('apc', sfGross))
        ataSeries.data.push(this.calcComponent('ata', sfGross))
        udbSeries.data.push(this.calcComponent('udb_pss', sfGross))
        fdbSeries.data.push(this.calcComponent('fdb_pss', sfGross))
        flsSeries.data.push(this.calcComponent('fls_ass', sfGross))
        uflsSeries.data.push(this.calcComponent('ufls_ass', sfGross))
        employerComponentSeries.data.push(this.calcComponent('employerComponent', sfGross))
       
        totalBenefitSeries.data.push(this.calcTotalBenefitPost(sfGross))
      })

      switch (reportType) {
        case 'contributor':
          this.memberSeriesOne = [ amcSeries, apcSeries, employerComponentSeries, ataSeries, totalBenefitSeries]
          break;
        case 'preserved':
          this.memberSeriesOne = [ udbSeries, fdbSeries, ataSeries, totalBenefitSeries]
          break;
        case 'associate_preserved':
          this.memberSeriesOne = [ flsSeries, uflsSeries, totalBenefitSeries ]
          break;
        default: 
          this.memberSeriesOne = []
      }
    },
    graphMemberTwo () {
      let { labels, reportType } = this
      let bamSeries = { name: labels.member.bamPost, type: 'line', data: [] }
   

      this.xValues.forEach(percent => {
        let separationAmount = this.calcSeparationAmount(percent)
        let sfGross = this.calcSeparationFactorGross(separationAmount)

        bamSeries.data.push(this.calcBamReductionPost(sfGross))
      })

      switch (reportType) {
        case 'contributor':
            this.memberSeriesTwo = [ bamSeries ]
          break;
        default: 
          this.memberSeriesTwo = [ ]
      }
    },
    calcComponent (compId, sfGross) {
      if (sfGross === undefined) sfGross = this.calcSeparationFactorGross()

      if (compId === 'employerComponent') {
        return ((this.bam * this.data.as) * (1 - sfGross)) - this.calcComponent('amc', sfGross) - this.calcComponent('apc', sfGross)
      } else {
        return (this.data[compId] || 0) * (1 - sfGross)
      }
    },
    calcBamReduction (sfGross) {
      if (sfGross === undefined) sfGross = this.calcSeparationFactorGross()
      return this.bam * sfGross
    },
    calcBamReductionPost (bamReduction) {
      if (bamReduction === undefined) bamReduction = this.calcBamReduction()
      return this.bam - bamReduction
    },
    calcTotalBenefitPost (sfGross) {
      if (sfGross === undefined) sfGross = this.calcSeparationFactorGross()

      let amc = this.calcComponent('amc', sfGross)
      let apc = this.calcComponent('apc', sfGross)
      let ata = this.calcComponent('ata', sfGross)
      let udb = this.calcComponent('udb_pss', sfGross)
      let fdb = this.calcComponent('fdb_pss', sfGross)
      let fls_ass = this.calcComponent('fls_ass', sfGross)
      let ufls_ass = this.calcComponent('ufls_ass', sfGross)
      let employerComponent = this.calcComponent('employerComponent', sfGross)

      switch (this.reportType) {
        case 'contributor': return amc + apc + ata + employerComponent
        case 'preserved': return udb + fdb + ata
        case 'associate_preserved': return fls_ass + ufls_ass
      }
    }
  }
}
</script>

<style lang="css">

</style>
